import { apiFetch } from "@/lib/api";
import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Volume2, Loader2, Copy, Check, Languages, FileText, Eye, Package, Star } from "lucide-react";


const VOICES = [
  { id: "nova", label: "Nova (Energetic)" },
  { id: "alloy", label: "Alloy (Neutral)" },
  { id: "shimmer", label: "Shimmer (Bright)" },
  { id: "echo", label: "Echo (Calm)" },
  { id: "fable", label: "Fable (Storytelling)" },
  { id: "onyx", label: "Onyx (Deep)" },
];

export default function TranslationResult({ result, targetLanguage, autoPlay }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [playingItem, setPlayingItem] = useState(null);
  const [selectedVoice, setSelectedVoice] = useState("nova");
  const [copiedField, setCopiedField] = useState(null);
  const autoPlayedRef = useRef(null);

  const handleTTS = async (text, itemId) => {
    if (isPlaying) return;
    setIsPlaying(true);
    setPlayingItem(itemId || "general");

    try {
      const response = await apiFetch(`/api/translate/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ text, voice: selectedVoice, speed: 1.0, language: targetLanguage }),
      });
      if (!response.ok) throw new Error("TTS failed");
      const data = await response.json();
      const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
      audio.onended = () => { setIsPlaying(false); setPlayingItem(null); };
      audio.onerror = () => { setIsPlaying(false); setPlayingItem(null); toast.error("Failed to play audio"); };
      await audio.play();
    } catch {
      toast.error("Text-to-speech failed");
      setIsPlaying(false);
      setPlayingItem(null);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
      toast.success("Copied to clipboard!");
    });
  };

  const hasText = result.extracted_text && result.extracted_text.trim();
  const hasMainObject = result.main_object && result.main_object.trim();
  const hasObjects = result.translated_objects && result.translated_objects.length > 0;

  // Separate main object from other objects
  const otherObjects = hasObjects
    ? result.translated_objects.filter(
        (obj) => obj.name?.toLowerCase() !== result.main_object?.toLowerCase()
      )
    : [];

  // Auto-play TTS for main object translation when result arrives
  useEffect(() => {
    if (!autoPlay) return;
    const textToPlay = result.main_object_translation || result.translated_text;
    const resultKey = result.translation_id || textToPlay;
    if (!textToPlay || autoPlayedRef.current === resultKey) return;
    autoPlayedRef.current = resultKey;

    const play = async () => {
      try {
        setIsPlaying(true);
        setPlayingItem("main-obj");
        const response = await apiFetch(`/api/translate/tts`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          
          body: JSON.stringify({ text: textToPlay, voice: selectedVoice, speed: 1.0, language: targetLanguage }),
        });
        if (!response.ok) throw new Error("TTS failed");
        const data = await response.json();
        const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
        audio.onended = () => { setIsPlaying(false); setPlayingItem(null); };
        audio.onerror = () => { setIsPlaying(false); setPlayingItem(null); };
        await audio.play();
      } catch {
        setIsPlaying(false);
        setPlayingItem(null);
      }
    };
    play();
  }, [result.translation_id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6" data-testid="translation-result">
      <div className="bg-white rounded-3xl border border-slate-100 shadow-xl shadow-indigo-100/20 p-6 md:p-8">
        {/* Header with voice selector */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-extrabold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Translation Result
          </h2>
          <Select value={selectedVoice} onValueChange={setSelectedVoice}>
            <SelectTrigger data-testid="voice-selector" className="w-[180px] rounded-xl border-2 border-slate-100 bg-slate-50 h-9 text-sm">
              <Volume2 className="w-4 h-4 mr-2 text-indigo-500" />
              <SelectValue placeholder="Voice" />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              {VOICES.map((v) => (
                <SelectItem key={v.id} value={v.id} className="text-sm">{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Main Object - Hero Display */}
        {hasMainObject && (
          <div
            className="relative mb-6 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-3xl border-2 border-indigo-100 p-6 md:p-8 overflow-hidden"
            data-testid="main-object-card"
          >
            <div className="absolute top-3 right-3">
              <span className="inline-flex items-center gap-1 bg-indigo-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md">
                <Star className="w-3 h-3" />
                Main Subject
              </span>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="flex-1">
                <p className="text-xs font-bold text-indigo-400 uppercase tracking-wider mb-1">Detected</p>
                <p
                  className="text-2xl sm:text-3xl font-black text-slate-900 capitalize"
                  style={{ fontFamily: 'Nunito, sans-serif' }}
                  data-testid="main-object-name"
                >
                  {result.main_object}
                </p>
              </div>
              <div className="w-px h-12 bg-indigo-200 hidden sm:block" />
              <div className="flex-1">
                <p className="text-xs font-bold text-purple-500 uppercase tracking-wider mb-1">{targetLanguage}</p>
                <p
                  className="text-2xl sm:text-3xl font-black text-indigo-700 capitalize"
                  style={{ fontFamily: 'Nunito, sans-serif' }}
                  data-testid="main-object-translation"
                >
                  {result.main_object_translation || "—"}
                </p>
              </div>
              <Button
                data-testid="play-main-object-btn"
                onClick={() =>
                  handleTTS(result.main_object_translation || result.main_object, "main-obj")
                }
                disabled={isPlaying}
                className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold shadow-lg hover:shadow-xl px-6 py-5 text-base hover:-translate-y-0.5 transition-all duration-300"
              >
                {playingItem === "main-obj" ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Volume2 className="w-5 h-5 mr-2" />
                )}
                Listen
              </Button>
            </div>
          </div>
        )}

        {/* Other Objects - Smaller Cards */}
        {otherObjects.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Eye className="w-5 h-5 text-pink-500" />
              <h3 className="text-base font-extrabold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Also in this image
              </h3>
              <span className="text-xs font-bold bg-pink-50 text-pink-600 px-2.5 py-0.5 rounded-full">
                {otherObjects.length} more
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {otherObjects.map((obj, i) => (
                <div
                  key={i}
                  className="group bg-gradient-to-br from-slate-50 to-indigo-50/30 rounded-2xl border border-slate-100 p-4 hover:border-indigo-200 hover:shadow-md transition-all duration-200"
                  data-testid={`object-card-${i}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <Package className="w-4 h-4 text-indigo-400" />
                    <button
                      data-testid={`play-object-${i}`}
                      onClick={() => handleTTS(obj.translation, `obj-${i}`)}
                      disabled={isPlaying}
                      className="w-7 h-7 rounded-full bg-indigo-100 hover:bg-indigo-200 flex items-center justify-center transition-colors disabled:opacity-50"
                    >
                      {playingItem === `obj-${i}` ? (
                        <Loader2 className="w-3.5 h-3.5 text-indigo-600 animate-spin" />
                      ) : (
                        <Volume2 className="w-3.5 h-3.5 text-indigo-600" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 font-medium capitalize">{obj.name}</p>
                  <p className="text-sm font-bold text-slate-900 mt-0.5 capitalize" style={{ fontFamily: 'Nunito, sans-serif' }}>
                    {obj.translation}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Text Translation Section */}
        {hasText && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-400" />
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Original Text</span>
                </div>
                <Button
                  data-testid="copy-original-btn"
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(result.extracted_text, "original")}
                  className="text-slate-400 hover:text-indigo-600 rounded-full h-7 w-7 p-0"
                >
                  {copiedField === "original" ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                </Button>
              </div>
              <div className="bg-slate-50 rounded-2xl p-4 min-h-[100px] border border-slate-100">
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="original-text">
                  {result.extracted_text}
                </p>
              </div>
              <Button
                data-testid="play-original-btn"
                variant="outline"
                size="sm"
                onClick={() => handleTTS(result.extracted_text, "original")}
                disabled={isPlaying}
                className="rounded-full border-2 border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 font-semibold"
              >
                {playingItem === "original" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Volume2 className="w-4 h-4 mr-2" />}
                Listen
              </Button>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Languages className="w-4 h-4 text-indigo-500" />
                  <span className="text-xs font-bold text-indigo-500 uppercase tracking-wider">{targetLanguage}</span>
                </div>
                <Button
                  data-testid="copy-translated-btn"
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(result.translated_text, "translated")}
                  className="text-slate-400 hover:text-indigo-600 rounded-full h-7 w-7 p-0"
                >
                  {copiedField === "translated" ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                </Button>
              </div>
              <div className="bg-indigo-50/50 rounded-2xl p-4 min-h-[100px] border border-indigo-100">
                <p className="text-sm text-slate-900 font-medium leading-relaxed whitespace-pre-wrap" data-testid="translated-text">
                  {result.translated_text}
                </p>
              </div>
              <Button
                data-testid="play-translated-btn"
                onClick={() => handleTTS(result.translated_text, "translated")}
                disabled={isPlaying}
                className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-md hover:shadow-lg"
              >
                {playingItem === "translated" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Volume2 className="w-4 h-4 mr-2" />}
                Listen in {targetLanguage}
              </Button>
            </div>
          </div>
        )}

        {!hasText && hasMainObject && (
          <div className="bg-yellow-50 rounded-2xl p-4 border border-yellow-100 text-center">
            <p className="text-sm text-yellow-700 font-medium">
              No text was found in the image. Tap the speaker icon to hear pronunciation of detected objects!
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
